/**
 * A one-way wire from "she just did something" to every mark on screen.
 *
 * The alternative was threading a callback from the toast layer, the log
 * commit path and the ceremony host down into whatever component happens to
 * be rendering a mark today, which is four props for one blink. A counter in
 * a store is enough: marks subscribe, an increment is an event, and the value
 * itself is meaningless.
 *
 * Two signals, and the distinction between them is the whole point:
 *
 * - **flare** — she is speaking. Fired on the frame a persona toast arrives,
 *   so the voice visibly comes out of her rather than out of the bottom of
 *   the screen.
 * - **flicker** — she noticed and said nothing. Ordinary logs only speak two
 *   times in three (`LOG_SASS_RATE`), and the silent third used to be
 *   indistinguishable from the app having missed the entry. Now it is the
 *   difference between someone not listening and someone choosing not to
 *   comment.
 */

import { create } from 'zustand'

export type MarkSignal = 'flare' | 'flicker'

interface MarkSignalState {
  /** Increments on every signal; marks watch this to know something happened. */
  seq: number
  kind: MarkSignal
  /** Increments on every touch anywhere in the app. */
  tapSeq: number
  /** Viewport coordinates of that touch, so each mark can work out its own angle. */
  tapX: number
  tapY: number
}

export const useMarkSignal = create<MarkSignalState>(() => ({
  seq: 0,
  kind: 'flare',
  tapSeq: 0,
  tapX: 0,
  tapY: 0,
}))

function signal(kind: MarkSignal): void {
  useMarkSignal.setState((state) => ({ seq: state.seq + 1, kind }))
}

let watching = false

/**
 * Start noticing where the screen is being touched.
 *
 * She is a thing that watches the kitchen, and the cheapest way to say so is
 * for her to look at what you just did. One listener for the whole app, in the
 * capture phase so a tap that something else swallows still counts — she is
 * watching the room, not handling the event.
 *
 * Idempotent, and never removed: it is one passive listener for the lifetime of
 * the tab, and tearing it down when the last mark unmounts would mean adding
 * and removing it on every screen change.
 */
export function watchTaps(): void {
  if (watching || typeof document === 'undefined') return
  watching = true
  document.addEventListener(
    'pointerdown',
    (event) => {
      useMarkSignal.setState((state) => ({
        tapSeq: state.tapSeq + 1,
        tapX: event.clientX,
        tapY: event.clientY,
      }))
    },
    { capture: true, passive: true },
  )
}

/** She is speaking: call this as the line appears, not before it. */
export function pulseMark(): void {
  signal('flare')
}

/** She noticed and let it pass. */
export function flickerMark(): void {
  signal('flicker')
}
